import {
  AfterViewInit,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  signal,
  TemplateRef
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SiCardComponent } from '@siemens/element-ng/card';
import { SI_DATATABLE_CONFIG, SiDatatableModule } from '@siemens/element-ng/datatable';
import { SiEmptyStateModule } from '@siemens/element-ng/empty-state';
import { SiFormModule } from '@siemens/element-ng/form';
import { SiHelpButtonComponent } from '@siemens/element-ng/help-button';
import { SiModalService } from '@siemens/element-ng/modal';
import { SiNumberInputModule } from '@siemens/element-ng/number-input';
import { SiPopoverBodyDirective, SiPopoverDirective } from '@siemens/element-ng/popover';
import { NgxDatatableModule } from '@siemens/ngx-datatable';
import { Subscription } from 'rxjs';

import { STANDARD_MODAL_CONFIG } from '../../shared/const';
import { Scenario, ScenarioParameters, Vehicle } from '../../shared/DataTypes';
import { EventService } from '../../shared/services/event.service';
import { ShareDataService } from '../../shared/services/share-data.service';
import { LoadModalComponent } from '../schedule-and-track/load-modal/load-modal.component';
import { VehicleService } from './vehicle.service';

@Component({
  selector: 'app-vehicle',
  imports: [
    TranslateModule,
    SiCardComponent,
    SiNumberInputModule,
    FormsModule,
    SiFormModule,
    SiDatatableModule,
    NgxDatatableModule,
    SiEmptyStateModule,
    LoadModalComponent,
    SiPopoverDirective,
    SiPopoverBodyDirective,
    SiHelpButtonComponent
  ],
  templateUrl: './vehicle.component.html',
  styleUrl: './vehicle.component.scss',
  host: {
    class: 'si-layout-inner'
  }
})
export class VehicleComponent implements AfterViewInit, OnDestroy {
  vehicleService = inject(VehicleService);
  eventService = inject(EventService);
  shareDataService = inject(ShareDataService);
  modalService = inject(SiModalService);

  constructor() {
    effect(() => {
      this.shareDataService.setVehicleSimulationData({
        vehicleId: this.selectedVehicle()?.id ?? -1,
        scenario: this.selectedScenario()!,
        drivingStyle: this.selectedDrivingStyle(),
        availableFuel: this.selectedAvailableFuel()
      });
    });

    effect(() => {
      // Only set selected vehicle the first time workspaces arrive
      const workspaces = this.availableWorkspaces() ?? [];
      const publicWorkspace = workspaces.find(
        workspace => workspace.name?.trim().toLowerCase() === 'public'
      );
      const fallbackWorkspace = workspaces.find(workspace => workspace.vehicles?.length);
      const initialWorkspace = publicWorkspace ?? fallbackWorkspace;
      const initialVehicle = initialWorkspace?.vehicles?.[0];

      if (!this.hasInitializedSelectedVehicle && initialVehicle) {
        this.eventService.updateSelectedVehicle(
          this.vehicleService.getVehicleObjectFromWorkspaceVehicleData(initialVehicle)
        );
        this.hasInitializedSelectedVehicle = true;
      }
    });
  }

  private hasInitializedSelectedVehicle = false;
  private readonly eventSubscriptions = new Subscription();
  private tourVehicleSnapshot: {
    vehicle: Vehicle | undefined;
    scenario: Scenario | undefined;
    actions: { drivingActions: string[]; stoppingActions: string[] };
  } | null = null;

  ngAfterViewInit(): void {
    this.eventSubscriptions.add(
      this.eventService.selectedVehicleChangedEvent$.subscribe((vehicle: Vehicle | null) => {
        if (vehicle) {
          this.selectedVehicle.set(vehicle);
          this.selectedScenario.set(this.selectedVehicle()!.scenarios[0]);
          this.updateActionsForVehicle();
        }
      })
    );

    this.eventSubscriptions.add(
      this.eventService.loadTourVehicleEvent$.subscribe(vehicle => {
        this.tourVehicleSnapshot ??= {
          vehicle: this.selectedVehicle(),
          scenario: this.selectedScenario(),
          actions: this.eventService.getLastActionsForVehicle()
        };
        this.eventService.updateSelectedVehicle(vehicle);
      })
    );

    this.eventSubscriptions.add(
      this.eventService.clearTourDataEvent$.subscribe(() => {
        if (!this.tourVehicleSnapshot) {
          return;
        }

        this.selectedVehicle.set(this.tourVehicleSnapshot.vehicle);
        this.selectedScenario.set(this.tourVehicleSnapshot.scenario);
        this.eventService.updateActionsForVehicle(this.tourVehicleSnapshot.actions);
        this.tourVehicleSnapshot = null;
      })
    );
  }

  ngOnDestroy(): void {
    this.eventSubscriptions.unsubscribe();
  }

  tableConfig = SI_DATATABLE_CONFIG;

  readonly serviceVehicleError = signal<Error | undefined>(undefined);

  readonly availableWorkspaces = this.shareDataService.availableWorkspaces;

  readonly selectedVehicle = signal<Vehicle | undefined>(undefined);

  readonly selectedScenario = signal<Scenario | undefined>(undefined);
  readonly scenarioParameters = signal<ScenarioParameters | undefined>(undefined);
  readonly scenarioParametersLoading = signal(false);
  readonly scenarioParameterSections = signal<
    { heading: string; params: { key: string; value: string }[] }[]
  >([]);

  drivingStyles: { value: string; name: string }[] = [
    {
      value: 'fahrt_nach_fahrplan',
      name: 'VEHICLE.DRIVING_STYLES.AS_SCHEDULED'
    },
    { value: 'spitzfahrt', name: 'VEHICLE.DRIVING_STYLES.FAST' },
    { value: 'spitzfahrt_nach_fahrplan', name: 'VEHICLE.DRIVING_STYLES.FAST_AS_SCHEDULED' }
  ];
  readonly selectedDrivingStyle = signal(this.drivingStyles[0].value);

  // BEMU: SOE
  // HEMU: H2
  // EMU:  no fuel input needed
  readonly selectedAvailableFuel = signal(100);
  readonly fuelInputLabel = computed(() => {
    const vehicleType = this.selectedVehicle()?.type;
    if (vehicleType === 'BEMU') {
      return 'VEHICLE.AVAILABLE_SOE';
    } else if (vehicleType === 'HEMU') {
      return 'VEHICLE.AVAILABLE_H2';
    } else {
      throw new Error(`Unexpected vehicle type: ${vehicleType}`);
    }
  });
  readonly vehicleNeedsFuelInput = computed(() => {
    return this.selectedVehicle()?.type !== 'EMU';
  });

  updateActionsForVehicle() {
    this.eventService.updateActionsForVehicle({
      drivingActions: this.selectedScenario()!.drivingActions,
      stoppingActions: this.selectedScenario()!.stopActions
    });
  }

  openModal(template: TemplateRef<any>): void {
    this.modalService.show(template, {
      ...STANDARD_MODAL_CONFIG,
      class: 'medium-modal'
    });
  }

  // Wrapper to ensure the vehicle info modal only opens when a vehicle is selected
  openVehicleInfo(template: TemplateRef<any>): void {
    if (!this.selectedVehicle()) {
      console.warn('No vehicle selected; vehicle info modal will not open.');
      return;
    }
    console.debug('Opening vehicle info modal for', this.selectedVehicle()?.name);
    this.fetchScenarioParameters();
    this.modalService.show(template, {
      ...STANDARD_MODAL_CONFIG,
      class: 'modal-dialog-centered modal-lg'
    });
  }

  fetchScenarioParameters(): void {
    const vehicle = this.selectedVehicle();
    const scenario = this.selectedScenario();
    if (!vehicle || !scenario) {
      return;
    }
    this.scenarioParametersLoading.set(true);
    this.scenarioParameters.set(undefined);
    this.vehicleService.getScenarioParameters(vehicle.id, scenario.name).subscribe({
      next: params => {
        this.scenarioParameters.set(params);
        this.scenarioParametersLoading.set(false);
        this.buildModalTableRows(params);
      },
      error: err => {
        console.error('Failed to load scenario parameters', err);
        this.scenarioParametersLoading.set(false);
      }
    });
  }

  private readonly sectionOrder: (keyof ScenarioParameters)[] = [
    'mechanical_properties',
    'driving_properties',
    'auxiliary_converter_properties'
  ];

  private buildModalTableRows(params: ScenarioParameters): void {
    const sections: { heading: string; params: { key: string; value: string }[] }[] = [];

    for (const sectionKey of this.sectionOrder) {
      const data = params[sectionKey];
      if (!data) {
        continue;
      }
      const entries = Object.entries(data)
        .filter(([key]) => key !== 'parameter_name')
        .map(([key, value]) => ({
          key: this.formatKey(key),
          value: String(value)
        }));

      sections.push({ heading: this.formatKey(sectionKey), params: entries });
    }

    this.scenarioParameterSections.set(sections);
  }

  /** Convert snake_case keys into readable Title Case labels. */
  private formatKey(key: string): string {
    return key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  }

  closeModal(modalRef: any): void {
    modalRef.hide();
  }

  openVehicleClicked(vehicleData: any) {
    this.eventService.updateSelectedVehicle(
      this.vehicleService.getVehicleObjectFromWorkspaceVehicleData(vehicleData)
    );
  }
}
